/**
 * @krisspy-file
 * @type page
 * @name "MerchantSettings"
 * @title "Merchant Settings"
 * @description "Merchant configuration with tabbed interface for different settings categories"
 * @routes ["/settings"]
 * @pathExample "/settings"
 * @layout "DashboardLayout"
 * @flowName "merchant-settings"
 */

import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Settings,
  Info,
  Palette,
  Globe,
  FileText,
  Sliders,
  CreditCard,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle,
  Mail,
  Phone as PhoneIcon,
  MessageSquare,
  Clock,
  Upload,
  Trash2,
  Plus,
  Undo2,
  ExternalLink,
  RefreshCw,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getMerchantById, updateMerchant, upsertMerchantTranslation, deleteMerchantTranslation, getMerchantTranslations, type MerchantWithCurrency } from '../services/merchants.service';
import { useAuth } from '../contexts/AuthContext';
import { useMerchant } from '../contexts/MerchantContext';
import NumberInput from '../components/NumberInput';
import { uploadMerchantBrandAsset } from '../lib/storage';
import ReturnAppSettings from '../components/ReturnAppSettings';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { prepareHtmlForQuill, prepareHtmlForStorageOrNull } from '../utils/htmlConversion';

// Quill toolbar configuration for basic HTML formatting
const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ 'list': 'bullet' }],
    [{ 'align': [] }],
    ['link'],
    ['clean']
  ]
};

const quillFormats = [
  'bold', 'italic', 'underline',
  'list', 'bullet',
  'align',
  'link'
];

type TabType = 'general' | 'branding' | 'regional' | 'customText' | 'selfService' | 'paymentsPolicy' | 'returnApp';

interface CancellationPolicyRule {
  description: string;
  refund_percentage: number;
  hours_before_start: number;
}

type DepositRule = {
  category_slug: string;
  amount: number; // stored in cents
};

interface PaymentMethodOption {
  value: string;
  label: string;
}

interface StripeAccountStatus {
  connected: boolean;
  accountId: string | null;
  detailsSubmitted: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  onboardingComplete: boolean;
  dashboardUrl?: string | null;
  country?: string;
  defaultCurrency?: string;
  error?: string;
  activePaymentMethods?: PaymentMethodOption[];
}

const TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Vienna',
  'Europe/Rome',
  'Europe/Madrid',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Australia/Sydney',
];

const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
];

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'de', name: 'German' },
  { code: 'fr', name: 'French' },
  { code: 'es', name: 'Spanish' },
  { code: 'it', name: 'Italian' },
  { code: 'hu', name: 'Hungarian' },
  { code: 'fi', name: 'Finnish' },
];

// Stripe-supported countries for Express accounts
const STRIPE_COUNTRIES = [
  { code: 'AT', name: 'Austria' },
  { code: 'AU', name: 'Australia' },
  { code: 'BE', name: 'Belgium' },
  { code: 'CA', name: 'Canada' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'DE', name: 'Germany' },
  { code: 'DK', name: 'Denmark' },
  { code: 'ES', name: 'Spain' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IT', name: 'Italy' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NO', name: 'Norway' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'PT', name: 'Portugal' },
  { code: 'SE', name: 'Sweden' },
  { code: 'US', name: 'United States' },
];

export default function MerchantSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { merchantId, hasRole } = useAuth();
  const { refreshMerchant } = useMerchant();
  const { currentLanguage, defaultLanguage, availableLanguages } = useLanguage();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType>('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [merchant, setMerchant] = useState<MerchantWithCurrency | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState<string>('');

  // Stripe Connect state
  const [stripeStatus, setStripeStatus] = useState<StripeAccountStatus | null>(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeActivating, setStripeActivating] = useState(false);

  // PayPal Direct state
  const [paypalStatus, setPaypalStatus] = useState<{
    connected: boolean;
    paypalMerchantId: string | null;
    paypalEmail: string | null;
    isActive: boolean;
    paypalDirectEnabled: boolean;
    loading: boolean;
  }>({ connected: false, paypalMerchantId: null, paypalEmail: null, isActive: false, paypalDirectEnabled: false, loading: false });
  const [paypalMerchantIdInput, setPaypalMerchantIdInput] = useState('');
  const [paypalEmailInput, setPaypalEmailInput] = useState('');
  const [paypalConnecting, setPaypalConnecting] = useState(false);
  const [paypalDisconnecting, setPaypalDisconnecting] = useState(false);

  // Form state
  const [formData, setFormData] = useState<Partial<MerchantWithCurrency>>({});

  // Translation state
  const [activeLanguageTab, setActiveLanguageTab] = useState(defaultLanguage);
  const [translations, setTranslations] = useState<Record<string, {
    business_name: string;
    description: string;
    additional_text_checkout: string;
    cancellation_policy_description: string;
    custom_rental_periods_text: string;
  }>>({});

  // Cancellation policy state
  const [cancellationRules, setCancellationRules] = useState<CancellationPolicyRule[]>([]);

  // Deposit rules state
  const [depositRules, setDepositRules] = useState<DepositRule[]>([]);

  // File upload state
  const [uploadingTerms, setUploadingTerms] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadMerchant();
  }, [merchantId, defaultLanguage]);

  // Helper function to parse cancellation policy from merchant data
  const parseCancellationPolicy = (merchantData: MerchantWithCurrency) => {
    if (merchantData.cancellation_policy) {
      try {
        console.log('Raw cancellation_policy:', merchantData.cancellation_policy);

        const policy = typeof merchantData.cancellation_policy === 'string'
          ? JSON.parse(merchantData.cancellation_policy)
          : merchantData.cancellation_policy;

        console.log('Parsed policy:', policy);

        // Handle different JSONB structures
        let rules: CancellationPolicyRule[] = [];

        if (policy.rules && Array.isArray(policy.rules)) {
          // New format: { currency: "USD", rules: [...] }
          rules = policy.rules;
        } else if (Array.isArray(policy)) {
          // Direct array format: [...]
          rules = policy;
        } else if (policy.refund_percentage !== undefined && policy.hours_before_start !== undefined) {
          // Single rule format: { currency: "USD", refund_percentage: 80, hours_before_start: 2 }.
          // The optional legacy `description` key is read for backward compatibility
          // but is no longer authored here — prose lives in cancellation_policy_description.
          rules = [{
            description: policy.description || '',
            refund_percentage: policy.refund_percentage,
            hours_before_start: policy.hours_before_start,
          }];
          console.log('Converted single rule to array format');
        }

        console.log('Extracted rules:', rules);

        if (rules.length > 0) {
          setCancellationRules(rules);
        } else {
          setCancellationRules([]);
        }
      } catch (e) {
        console.error('Failed to parse cancellation policy:', e);
        setCancellationRules([]);
      }
    } else {
      console.log('No cancellation_policy found');
      setCancellationRules([]);
    }
  };

  const parseDepositRules = (merchantData: MerchantWithCurrency) => {
    if (merchantData.deposit_rules) {
      try {
        const rules = typeof merchantData.deposit_rules === 'string'
          ? JSON.parse(merchantData.deposit_rules)
          : merchantData.deposit_rules;

        if (Array.isArray(rules)) {
          setDepositRules(rules as DepositRule[]);
        } else {
          setDepositRules([]);
        }
      } catch (e) {
        console.error('Failed to parse deposit_rules:', e);
        setDepositRules([]);
      }
    } else {
      setDepositRules([]);
    }
  };

  const loadMerchant = async () => {
    if (!merchantId) {
      setErrors({ general: t('settings.noMerchant') });
      setLoading(false);
      return;
    }

    setLoading(true);
    const { data, error } = await getMerchantById(merchantId, defaultLanguage, defaultLanguage);

    if (error) {
      setErrors({ general: error });
      setLoading(false);
      return;
    }

    if (data) {
      setMerchant(data);
      // Fall back to the legacy cancellation_policy.description (pre-migration
      // data) if the flat column hasn't been populated yet.
      const cancellationDescription =
        data.cancellation_policy_description ||
        (data.cancellation_policy && typeof data.cancellation_policy === 'object'
          ? ((data.cancellation_policy as { description?: string }).description ?? null)
          : null);
      setFormData({
        ...data,
        cancellation_policy_description: cancellationDescription,
        additional_text_checkout: prepareHtmlForQuill(data.additional_text_checkout),
      });
      parseCancellationPolicy(data);
      parseDepositRules(data);

      // Seed existing translations so previously-saved values are visible and
      // re-editable (applies to all translatable merchant fields).
      const { data: existingTranslations } = await getMerchantTranslations(merchantId);
      if (existingTranslations && existingTranslations.length > 0) {
        const seeded: Record<string, {
          business_name: string;
          description: string;
          additional_text_checkout: string;
          cancellation_policy_description: string;
          custom_rental_periods_text: string;
        }> = {};
        for (const tr of existingTranslations) {
          seeded[tr.language_code] = {
            business_name: tr.business_name || '',
            description: tr.description || '',
            additional_text_checkout: prepareHtmlForQuill(tr.additional_text_checkout),
            cancellation_policy_description: tr.cancellation_policy_description || '',
            custom_rental_periods_text: tr.custom_rental_periods_text || '',
          };
        }
        setTranslations(seeded);
      }
    }

    setLoading(false);
  };

  // Fetch Stripe account status
  const fetchStripeStatus = async () => {
    if (!merchantId) return;

    setStripeLoading(true);
    try {
      const response = await fetch(`/api/stripe/get-account-status?merchantId=${merchantId}`);
      const result = await response.json();

      if (result.success) {
        setStripeStatus(result.data);
      } else {
        console.error('Failed to fetch Stripe status:', result.message);
      }
    } catch (error) {
      console.error('Error fetching Stripe status:', error);
    } finally {
      setStripeLoading(false);
    }
  };

  // Handle Stripe activation - create account and redirect to onboarding
  const handleStripeActivation = async () => {
    if (!merchant?.id) return;

    setStripeActivating(true);
    try {
      const response = await fetch('/api/stripe/create-account-link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ merchantId: merchant.id }),
      });

      const result = await response.json();

      if (result.success && result.url) {
        // Redirect to Stripe onboarding
        window.location.href = result.url;
      } else {
        setErrors({ stripe: result.message || 'Failed to create payment activation link' });
      }
    } catch (error: any) {
      console.error('Error activating payments:', error);
      setErrors({ stripe: error.message || 'Failed to activate payments' });
    } finally {
      setStripeActivating(false);
    }
  };

  // Fetch PayPal account status
  const fetchPaypalStatus = async () => {
    if (!merchantId) return;

    setPaypalStatus(prev => ({ ...prev, loading: true }));
    try {
      const response = await fetch(`/api/paypal/get-merchant-status?merchantId=${merchantId}`);
      const result = await response.json();
      setPaypalStatus({
        connected: result.connected || false,
        paypalMerchantId: result.paypalMerchantId || null,
        paypalEmail: result.paypalEmail || null,
        isActive: result.isActive || false,
        paypalDirectEnabled: result.paypalDirectEnabled || false,
        loading: false,
      });
    } catch (error) {
      console.error('Error fetching PayPal status:', error);
      setPaypalStatus(prev => ({ ...prev, loading: false }));
    }
  };

  // Connect PayPal merchant account
  const handlePaypalConnect = async () => {
    if (!merchantId || !paypalMerchantIdInput.trim()) return;

    setPaypalConnecting(true);
    try {
      const response = await fetch('/api/paypal/connect-merchant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          merchantId: merchantId,
          paypalMerchantId: paypalMerchantIdInput.trim(),
          paypalEmail: paypalEmailInput.trim() || undefined,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSuccessMessage(t('settings.paypalConnected', 'PayPal account connected successfully.'));
        setPaypalMerchantIdInput('');
        setPaypalEmailInput('');
        await fetchPaypalStatus();
      } else {
        setErrors({ paypal: result.message || 'Failed to connect PayPal account' });
      }
    } catch (error: any) {
      setErrors({ paypal: error.message || 'Failed to connect PayPal account' });
    } finally {
      setPaypalConnecting(false);
    }
  };

  // Disconnect PayPal merchant account
  const handlePaypalDisconnect = async () => {
    if (!merchantId) return;

    if (!window.confirm(t('settings.paypalDisconnectConfirm', 'Are you sure you want to disconnect your PayPal account? PayPal payments will no longer be available.'))) {
      return;
    }

    setPaypalDisconnecting(true);
    try {
      const response = await fetch('/api/paypal/disconnect-merchant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ merchantId: merchantId }),
      });

      const result = await response.json();

      if (result.success) {
        setSuccessMessage(t('settings.paypalDisconnected', 'PayPal account disconnected.'));
        await fetchPaypalStatus();
      } else {
        setErrors({ paypal: result.message || 'Failed to disconnect PayPal account' });
      }
    } catch (error: any) {
      setErrors({ paypal: error.message || 'Failed to disconnect PayPal account' });
    } finally {
      setPaypalDisconnecting(false);
    }
  };

  // Check for Stripe redirect params and fetch status
  useEffect(() => {
    const stripeParam = searchParams.get('stripe');
    if (stripeParam === 'success') {
      setActiveTab('paymentsPolicy');
      setSuccessMessage(t('settings.paymentSetupCompleted', 'Payment setup completed! Your account is being verified.'));
      // Clear the URL param
      searchParams.delete('stripe');
      setSearchParams(searchParams, { replace: true });
      // Refresh status
      fetchStripeStatus();
      setTimeout(() => setSuccessMessage(''), 5000);
    } else if (stripeParam === 'refresh') {
      setActiveTab('paymentsPolicy');
      // User needs to retry onboarding
      setErrors({ stripe: t('settings.paymentSetupNotCompleted', 'Payment setup was not completed. Please try again.') });
      searchParams.delete('stripe');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams]);

  // Fetch Stripe and PayPal status when merchant is loaded
  useEffect(() => {
    if (merchant?.id) {
      fetchStripeStatus();
      fetchPaypalStatus();
    }
  }, [merchant?.id]);

  const handleSave = async () => {
    if (!merchant) return;

    // Validate required fields
    const newErrors: Record<string, string> = {};

    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSaving(true);
    setErrors({});
    setSuccessMessage('');

    // Build cancellation policy JSON from first rule (flat structure).
    // The customer-facing prose now lives in the flat, translatable
    // cancellation_policy_description column (and merchant_translations),
    // so the JSONB stays language-neutral — structured refund rules only.
    const cancellationPolicy = cancellationRules.length > 0
      ? {
          currency: formData.currency_code || 'USD',
          refund_percentage: cancellationRules[0].refund_percentage,
          hours_before_start: cancellationRules[0].hours_before_start,
        }
      : null;

    console.log('cancellationRules:', cancellationRules);
    console.log('Built cancellationPolicy:', cancellationPolicy);

    const updatedFormData = {
      ...formData,
      additional_text_checkout: prepareHtmlForStorageOrNull(formData.additional_text_checkout),
      cancellation_policy: cancellationPolicy,
      deposit_rules: formData.deposits_enabled && depositRules.length > 0
        ? depositRules.filter(r => r.category_slug.trim() !== '')
        : null,
    };

    console.log('updatedFormData.cancellation_policy:', updatedFormData.cancellation_policy);

    const { data, error } = await updateMerchant(merchant.id, updatedFormData);

    if (error) {
      setErrors({ general: error });
      setSaving(false);
      return;
    }

    if (data) {
      setMerchant(data);
      setFormData({
        ...data,
        additional_text_checkout: prepareHtmlForQuill(data.additional_text_checkout),
      });
      parseCancellationPolicy(data);
      parseDepositRules(data);

      // Clean up any stale default-language translation row so it can't
      // override the freshly-updated main-table value on later reads.
      if (defaultLanguage) {
        await deleteMerchantTranslation(merchant.id, defaultLanguage);
      }

      // Save translations for all languages (except default). A language whose
      // fields are all empty is deleted so clearing a translation persists
      // instead of leaving the previous row in place.
      for (const [langCode, translationData] of Object.entries(translations)) {
        if (langCode === defaultLanguage) continue;
        const hasContent =
          translationData.business_name ||
          translationData.description ||
          prepareHtmlForStorageOrNull(translationData.additional_text_checkout) ||
          translationData.cancellation_policy_description ||
          translationData.custom_rental_periods_text;
        if (hasContent) {
          const { error: translationError } = await upsertMerchantTranslation(
            merchant.id,
            langCode,
            {
              business_name: translationData.business_name || null,
              description: translationData.description || null,
              additional_text_checkout: prepareHtmlForStorageOrNull(translationData.additional_text_checkout),
              cancellation_policy_description: translationData.cancellation_policy_description || null,
              custom_rental_periods_text: translationData.custom_rental_periods_text || null,
            }
          );
          if (translationError) {
            console.error(`Failed to save ${langCode} translation:`, translationError);
          }
        } else {
          const { error: translationError } = await deleteMerchantTranslation(merchant.id, langCode);
          if (translationError) {
            console.error(`Failed to delete ${langCode} translation:`, translationError);
          }
        }
      }

      // Scanning (and anything else read from MerchantContext) follows the saved row.
      refreshMerchant();

      setSuccessMessage(t('settings.successMessage'));
      setTimeout(() => setSuccessMessage(''), 3000);
    }

    setSaving(false);
  };

  const updateField = (field: keyof MerchantWithCurrency, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle terms PDF upload
  const handleTermsUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      setErrors({ terms_pdf: 'Only PDF files are allowed' });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setErrors({ terms_pdf: 'File size must be less than 10MB' });
      return;
    }

    setUploadingTerms(true);
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.terms_pdf;
      return newErrors;
    });

    try {
      // Convert file to base64
      const fileData = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Upload via API endpoint (server-side with service role)
      const response = await fetch('/api/upload-terms-pdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileData,
          fileName: file.name,
          merchantId: merchant?.merchant_id || '',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Upload failed');
      }

      const data = await response.json();

      // Update form data with the new URL
      updateField('terms_pdf_url', data.url);
      setSuccessMessage(t('settings.termsPdfUploaded', 'Terms PDF uploaded successfully!'));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      console.error('Upload error:', error);
      setErrors({ terms_pdf: error.message || 'Failed to upload file' });
    } finally {
      setUploadingTerms(false);
    }
  };

  // Handle logo/favicon upload
  const handleBrandAssetUpload = async (
    file: File,
    assetType: 'logo' | 'favicon',
    fieldName: 'logo_url' | 'favicon_url'
  ) => {
    if (!merchant?.merchant_id) return;

    const setUploading = assetType === 'logo' ? setUploadingLogo : setUploadingFavicon;
    setUploading(true);

    try {
      const publicUrl = await uploadMerchantBrandAsset(file, merchant.merchant_id, assetType);
      updateField(fieldName, publicUrl);
    } catch (error: any) {
      console.error(`${assetType} upload error:`, error);
      setErrors({ [fieldName]: error.message || `Failed to upload ${assetType}` });
    } finally {
      setUploading(false);
      if (assetType === 'logo' && logoInputRef.current) {
        logoInputRef.current.value = '';
      } else if (assetType === 'favicon' && faviconInputRef.current) {
        faviconInputRef.current.value = '';
      }
    }
  };

  // Add new cancellation rule
  const addCancellationRule = () => {
    setCancellationRules(prev => [
      ...prev,
      { description: '', refund_percentage: 100, hours_before_start: 24 },
    ]);
  };

  // Remove cancellation rule
  const removeCancellationRule = (index: number) => {
    setCancellationRules(prev => prev.filter((_, i) => i !== index));
  };

  // Update cancellation rule
  const updateCancellationRule = (
    index: number,
    field: keyof CancellationPolicyRule,
    value: string | number
  ) => {
    setCancellationRules(prev =>
      prev.map((rule, i) =>
        i === index ? { ...rule, [field]: value } : rule
      )
    );
  };

  // Deposit rule helpers
  const addDepositRule = () => {
    setDepositRules(prev => [...prev, { category_slug: '', amount: 0 }]);
  };

  const removeDepositRule = (index: number) => {
    setDepositRules(prev => prev.filter((_, i) => i !== index));
  };

  const updateDepositRule = (
    index: number,
    field: keyof DepositRule,
    value: string | number
  ) => {
    setDepositRules(prev =>
      prev.map((rule, i) =>
        i === index ? { ...rule, [field]: value } : rule
      )
    );
  };

  const tabs = [
    { id: 'general' as TabType, label: t('settings.general'), icon: Info },
    { id: 'branding' as TabType, label: t('settings.branding'), icon: Palette },
    { id: 'regional' as TabType, label: t('settings.regional'), icon: Globe },
    { id: 'customText' as TabType, label: t('settings.customText'), icon: FileText },
    { id: 'selfService' as TabType, label: t('settings.selfService'), icon: Sliders },
    { id: 'paymentsPolicy' as TabType, label: t('settings.paymentsPolicy'), icon: CreditCard },
    { id: 'returnApp' as TabType, label: t('settings.returnApp'), icon: Undo2 },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue" />
      </div>
    );
  }

  if (errors.general && !merchant) {
    return (
      <div className="p-lg">
        <div className="card p-lg text-center">
          <AlertCircle className="w-12 h-12 text-danger mx-auto mb-md" />
          <h2 className="text-lg font-semibold text-primary mb-sm">{t('settings.errorLoadingSettings', 'Error Loading Settings')}</h2>
          <p className="text-secondary">{errors.general}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2xl animate-fade-in">
      {/* Header */}
      <div className="card p-lg page-header" data-testid="merchant-settings-page-header">
        <div className="page-header-title flex items-center gap-md">
          <div className="w-10 h-10 rounded-lg bg-blue/10 flex items-center justify-center">
            <Settings className="w-6 h-6 text-blue" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-primary">{t('settings.merchantSettings')}</h1>
            <p className="text-sm text-secondary">{t('settings.configureBusinessSettings', 'Configure your business settings')}</p>
          </div>
        </div>
        <div className="page-header-actions" data-testid="merchant-settings-page-header-actions">
          {successMessage && (
            <div className="flex items-center gap-sm text-success bg-success/10 px-md py-sm rounded-lg">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm font-medium">{successMessage}</span>
            </div>
          )}
          {activeTab !== 'returnApp' && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn btn-primary flex items-center gap-sm"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('common.saving')}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {t('settings.saveSettings')}
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="card p-0 overflow-hidden">
        <div className="flex border-b border-primary overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-lg py-md text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-sm ${
                  activeTab === tab.id
                    ? 'text-blue border-b-2 border-blue bg-blue/5'
                    : 'text-secondary hover:text-primary hover:bg-secondary'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="p-lg">
          {/* General Tab */}
          {activeTab === 'general' && (
            <div className="space-y-lg">
              <div>
                <h3 className="text-base font-semibold text-primary mb-md">{t('settings.businessInformation')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-lg">
                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('settings.merchantId', 'Merchant ID')} <span className="text-secondary text-xs">({t('settings.readOnly', 'read-only')})</span>
                    </label>
                    <input
                      type="text"
                      value={formData.merchant_id || ''}
                      disabled
                      className="input w-full bg-secondary opacity-60 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('settings.businessName')}
                    </label>
                    <input
                      type="text"
                      value={formData.business_name || ''}
                      onChange={(e) => updateField('business_name', e.target.value)}
                      className="input w-full"
                      placeholder={t('settings.businessNamePlaceholder', 'Legal business name')}
                    />
                    {errors.business_name && (
                      <p className="text-danger text-xs mt-xs flex items-center gap-xs">
                        <AlertCircle className="w-3 h-3" />
                        {errors.business_name}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('settings.displayName', 'Display Name')}
                    </label>
                    <input
                      type="text"
                      value={formData.display_name || ''}
                      onChange={(e) => updateField('display_name', e.target.value)}
                      className="input w-full"
                      placeholder={t('settings.displayNamePlaceholder', 'Public display name')}
                    />
                    {errors.display_name && (
                      <p className="text-danger text-xs mt-xs flex items-center gap-xs">
                        <AlertCircle className="w-3 h-3" />
                        {errors.display_name}
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('settings.description')}
                    </label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => updateField('description', e.target.value)}
                      className="input w-full"
                      rows={3}
                      placeholder={t('settings.descriptionPlaceholder', 'Business description')}
                    />
                  </div>
                </div>

                {/* Translations Section */}
                {availableLanguages.length > 1 && (
                  <div className="mt-lg">
                    <h4 className="text-sm font-semibold text-primary mb-md">{t('settings.translations', 'Translations')}</h4>

                    {/* Language tabs */}
                    <div className="flex gap-sm mb-lg overflow-x-auto">
                      {availableLanguages.map((lang) => (
                        <button
                          key={lang.code}
                          type="button"
                          onClick={() => setActiveLanguageTab(lang.code)}
                          className={`px-md py-sm rounded-md flex items-center gap-xs whitespace-nowrap transition-colors ${
                            activeLanguageTab === lang.code
                              ? 'bg-blue text-white'
                              : 'bg-secondary text-secondary hover:bg-primary/10'
                          }`}
                        >
                          <span className="text-lg">{lang.flag}</span>
                          <span className="text-sm font-medium">{lang.label}</span>
                        </button>
                      ))}
                    </div>

                    <div className="space-y-md">
                      {activeLanguageTab === defaultLanguage ? (
                        <div className="p-md bg-blue/5 border border-blue/20 rounded-lg space-y-sm">
                          <p className="text-sm text-blue">
                            <strong>{t('settings.businessName')}:</strong> {formData.business_name || <em>({t('common.none')})</em>}
                          </p>
                          <p className="text-sm text-blue">
                            <strong>{t('settings.description')}:</strong> {formData.description || <em>({t('common.none')})</em>}
                          </p>
                          <p className="text-xs text-secondary mt-md">
                            {t('settings.mainLanguageEditAbove', 'This is the main language. Edit the fields above to change these values.')}
                          </p>
                        </div>
                      ) : (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-primary mb-sm">
                              {t('settings.businessName')} ({activeLanguageTab.toUpperCase()})
                            </label>
                            <input
                              type="text"
                              value={translations[activeLanguageTab]?.business_name || ''}
                              onChange={(e) => setTranslations(prev => ({
                                ...prev,
                                [activeLanguageTab]: {
                                  business_name: e.target.value,
                                  description: prev[activeLanguageTab]?.description || '',
                                  additional_text_checkout: prev[activeLanguageTab]?.additional_text_checkout || '',
                                  cancellation_policy_description: prev[activeLanguageTab]?.cancellation_policy_description || '',
                                  custom_rental_periods_text: prev[activeLanguageTab]?.custom_rental_periods_text || ''
                                }
                              }))}
                              className="input w-full"
                              placeholder={`${t('settings.addTranslationVersion', 'Add the {{language}} version', { language: availableLanguages.find(l => l.code === activeLanguageTab)?.label })}`}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-primary mb-sm">
                              {t('settings.description')} ({activeLanguageTab.toUpperCase()})
                            </label>
                            <textarea
                              value={translations[activeLanguageTab]?.description || ''}
                              onChange={(e) => setTranslations(prev => ({
                                ...prev,
                                [activeLanguageTab]: {
                                  business_name: prev[activeLanguageTab]?.business_name || '',
                                  description: e.target.value,
                                  additional_text_checkout: prev[activeLanguageTab]?.additional_text_checkout || '',
                                  cancellation_policy_description: prev[activeLanguageTab]?.cancellation_policy_description || '',
                                  custom_rental_periods_text: prev[activeLanguageTab]?.custom_rental_periods_text || ''
                                }
                              }))}
                              className="input w-full"
                              rows={3}
                              placeholder={`${t('settings.addTranslationVersion', 'Add the {{language}} version', { language: availableLanguages.find(l => l.code === activeLanguageTab)?.label })}`}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="border-t border-primary pt-lg">
                <h3 className="text-base font-semibold text-primary mb-md">{t('settings.contactDetails')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-lg">
                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('settings.contactEmail')} <span className="text-danger">*</span>
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary" />
                      <input
                        type="email"
                        value={formData.email || ''}
                        onChange={(e) => updateField('email', e.target.value)}
                        className="input w-full pl-10"
                        placeholder={t('settings.emailPlaceholder', 'contact@business.com')}
                      />
                    </div>
                    {errors.email && (
                      <p className="text-danger text-xs mt-xs flex items-center gap-xs">
                        <AlertCircle className="w-3 h-3" />
                        {errors.email}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('settings.contactPhone')}
                    </label>
                    <div className="relative">
                      <PhoneIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary" />
                      <input
                        type="tel"
                        value={formData.phone || ''}
                        onChange={(e) => updateField('phone', e.target.value)}
                        className="input w-full pl-10"
                        placeholder={t('settings.phonePlaceholder', '+1 234 567 8900')}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('settings.website', 'Website')}
                    </label>
                    <input
                      type="url"
                      value={formData.website || ''}
                      onChange={(e) => updateField('website', e.target.value)}
                      className="input w-full"
                      placeholder={t('settings.websitePlaceholder', 'https://www.business.com')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('settings.country', 'Country')}
                    </label>
                    <select
                      value={formData.country_code || ''}
                      onChange={(e) => updateField('country_code', e.target.value)}
                      className="input w-full"
                    >
                      <option value="">{t('settings.selectCountry', 'Select country...')}</option>
                      {STRIPE_COUNTRIES.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-secondary mt-xs">
                      {t('settings.countryDesc', 'Used for regional date/time formatting and payment account setup')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t border-primary pt-lg">
                <h3 className="text-base font-semibold text-primary mb-md">{t('settings.notifications', 'Notifications')}</h3>
                <div className="space-y-md">
                  <label className="flex items-center gap-md cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.sms || false}
                      onChange={(e) => updateField('sms', e.target.checked)}
                      className="w-4 h-4 text-blue border-primary rounded focus:ring-blue"
                    />
                    <div className="flex items-center gap-sm">
                      <MessageSquare className="w-4 h-4 text-secondary" />
                      <span className="text-sm font-medium text-primary">{t('settings.smsNotifications', 'SMS Notifications')}</span>
                    </div>
                  </label>
                  <label className="flex items-center gap-md cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.whatsapp || false}
                      onChange={(e) => updateField('whatsapp', e.target.checked)}
                      className="w-4 h-4 text-blue border-primary rounded focus:ring-blue"
                    />
                    <div className="flex items-center gap-sm">
                      <MessageSquare className="w-4 h-4 text-secondary" />
                      <span className="text-sm font-medium text-primary">{t('settings.whatsappNotifications', 'WhatsApp Notifications')}</span>
                    </div>
                  </label>
                </div>
                {formData.sms && (
                  <div className="mt-md ml-8">
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('settings.smsSenderName', 'SMS Sender Name')}
                    </label>
                    <input
                      type="text"
                      value={formData.sms_sender_name || ''}
                      readOnly
                      className="input w-full max-w-md bg-gray-50 text-secondary cursor-not-allowed"
                    />
                    <p className="text-xs text-secondary mt-xs">
                      {t('settings.smsSenderNameHint', 'Configured by your SMS gateway provider. Contact support to change.')}
                    </p>
                  </div>
                )}
              </div>

              <div className="border-t border-primary pt-lg">
                <h3 className="text-base font-semibold text-primary mb-md">{t('settings.marketingConsent', 'Marketing Consent')}</h3>
                <p className="text-sm text-secondary mb-md">
                  {t('settings.consentDesc', 'Configure the consent checkbox shown during booking. Leave empty to disable the consent question.')}
                </p>
                {availableLanguages.length > 1 && (
                  <div className="flex gap-sm mb-lg overflow-x-auto">
                    {availableLanguages.map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => setActiveLanguageTab(lang.code)}
                        className={`px-md py-sm rounded-md flex items-center gap-xs whitespace-nowrap transition-colors ${
                          activeLanguageTab === lang.code
                            ? 'bg-blue text-white'
                            : 'bg-secondary text-secondary hover:bg-primary/10'
                        }`}
                      >
                        <span className="text-lg">{lang.flag}</span>
                        <span className="text-sm font-medium">{lang.label}</span>
                      </button>
                    ))}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-primary mb-sm">
                    {t('settings.consentLabel', 'Consent Label')} {availableLanguages.length > 1 ? `(${activeLanguageTab.toUpperCase()})` : ''}
                  </label>
                  <input
                    type="text"
                    value={
                      ((formData.consent_checkbox_label as Record<string, string>) || {})[activeLanguageTab] || ''
                    }
                    onChange={(e) => {
                      const current = (formData.consent_checkbox_label as Record<string, string>) || {};
                      const updated = { ...current };
                      if (e.target.value) {
                        updated[activeLanguageTab] = e.target.value;
                      } else {
                        delete updated[activeLanguageTab];
                      }
                      updateField('consent_checkbox_label', Object.keys(updated).length > 0 ? updated : null);
                    }}
                    className="input w-full"
                    placeholder={
                      {
                        en: 'e.g. I agree to receive marketing emails',
                        de: 'z.B. Ich stimme dem Erhalt von Marketing-E-Mails zu',
                        fr: 'ex. J\'accepte de recevoir des e-mails marketing',
                        es: 'ej. Acepto recibir correos electrónicos de marketing',
                        it: 'es. Accetto di ricevere email di marketing',
                        nl: 'bijv. Ik ga akkoord met het ontvangen van marketing e-mails',
                        pt: 'ex. Concordo em receber e-mails de marketing',
                      }[activeLanguageTab] || 'e.g. I agree to receive marketing emails'
                    }
                  />
                </div>
              </div>
            </div>
          )}

          {/* Branding Tab */}
          {activeTab === 'branding' && (
            <div className="space-y-lg">
              <div>
                <h3 className="text-base font-semibold text-primary mb-md">{t('settings.brandAssets', 'Brand Assets')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-lg">
                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('settings.logo')}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="url"
                        value={formData.logo_url || ''}
                        onChange={(e) => updateField('logo_url', e.target.value)}
                        className="input flex-1"
                        placeholder={t('settings.urlOrUpload', 'URL or upload an image')}
                      />
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleBrandAssetUpload(file, 'logo', 'logo_url');
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                        className="btn btn-secondary whitespace-nowrap"
                      >
                        <Upload className="w-4 h-4" />
                        {uploadingLogo ? t('settings.uploading', 'Uploading...') : t('settings.upload', 'Upload')}
                      </button>
                    </div>
                    {formData.logo_url && (
                      <div className="mt-2">
                        <img src={formData.logo_url} alt={t('settings.logoPreview', 'Logo preview')} className="w-24 h-12 object-contain border rounded" />
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('settings.logoWidth', 'Logo Width (40-200px)')}
                    </label>
                    <input
                      type="number"
                      min="40"
                      max="200"
                      value={formData.logo_width ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        updateField('logo_width', value === '' ? null : parseInt(value, 10));
                      }}
                      className="input w-full"
                      placeholder="40"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('settings.favicon')}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="url"
                        value={formData.favicon_url || ''}
                        onChange={(e) => updateField('favicon_url', e.target.value)}
                        className="input flex-1"
                        placeholder={t('settings.urlOrUpload', 'URL or upload an image')}
                      />
                      <input
                        ref={faviconInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleBrandAssetUpload(file, 'favicon', 'favicon_url');
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => faviconInputRef.current?.click()}
                        disabled={uploadingFavicon}
                        className="btn btn-secondary whitespace-nowrap"
                      >
                        <Upload className="w-4 h-4" />
                        {uploadingFavicon ? t('settings.uploading', 'Uploading...') : t('settings.upload', 'Upload')}
                      </button>
                    </div>
                    {formData.favicon_url && (
                      <div className="mt-2">
                        <img src={formData.favicon_url} alt={t('settings.faviconPreview', 'Favicon preview')} className="w-8 h-8 object-contain border rounded" />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="border-t border-primary pt-lg">
                <h3 className="text-base font-semibold text-primary mb-md">{t('settings.brandColors', 'Brand Colors')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-lg">
                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('settings.primaryColor')}
                    </label>
                    <div className="flex gap-md">
                      <input
                        type="color"
                        value={formData.color1 || '#3b82f6'}
                        onChange={(e) => updateField('color1', e.target.value)}
                        className="w-16 h-10 rounded border border-primary cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.color1 || '#3b82f6'}
                        onChange={(e) => updateField('color1', e.target.value)}
                        className="input flex-1"
                        placeholder="#3b82f6"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('settings.secondaryColor', 'Secondary Color')}
                    </label>
                    <div className="flex gap-md">
                      <input
                        type="color"
                        value={formData.color2 || '#10b981'}
                        onChange={(e) => updateField('color2', e.target.value)}
                        className="w-16 h-10 rounded border border-primary cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.color2 || '#10b981'}
                        onChange={(e) => updateField('color2', e.target.value)}
                        className="input flex-1"
                        placeholder="#10b981"
                      />
                    </div>
                  </div>
                </div>
              </div>

            </div>
          )}

          {/* Regional Settings Tab */}
          {activeTab === 'regional' && (
            <div className="space-y-lg">
              <div>
                <h3 className="text-base font-semibold text-primary mb-md">{t('settings.timezoneCurrency', 'Timezone & Currency')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-lg">
                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('settings.timezone')}
                    </label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary" />
                      <select
                        value={formData.timezone || 'UTC'}
                        onChange={(e) => updateField('timezone', e.target.value)}
                        className="input w-full pl-10"
                      >
                        {TIMEZONES.map((tz) => (
                          <option key={tz} value={tz}>
                            {tz}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('settings.currency')}
                    </label>
                    <select
                      value={formData.currency_code || 'USD'}
                      onChange={(e) => {
                        const currency = CURRENCIES.find(c => c.code === e.target.value);
                        updateField('currency_code', currency?.code);
                        updateField('currency_symbol', currency?.symbol);
                      }}
                      className="input w-full"
                    >
                      {CURRENCIES.map((currency) => (
                        <option key={currency.code} value={currency.code}>
                          {currency.name} ({currency.symbol})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('settings.currencySymbol', 'Currency Symbol')}
                    </label>
                    <input
                      type="text"
                      value={formData.currency_symbol || '$'}
                      onChange={(e) => updateField('currency_symbol', e.target.value)}
                      className="input w-full"
                      placeholder="$"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t border-primary pt-lg">
                <h3 className="text-base font-semibold text-primary mb-md">{t('settings.languageSettings', 'Language Settings')}</h3>
                <div>
                  <label className="block text-sm font-medium text-primary mb-sm">
                    {t('settings.supportedLanguages', 'Supported Languages')}
                  </label>
                  <div className="space-y-sm">
                    {LANGUAGES.map((lang) => (
                      <label key={lang.code} className="flex items-center gap-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={(formData.supported_languages || ['en']).includes(lang.code)}
                          onChange={(e) => {
                            const current = formData.supported_languages || ['en'];
                            if (e.target.checked) {
                              updateField('supported_languages', [...current, lang.code]);
                            } else {
                              updateField('supported_languages', current.filter(l => l !== lang.code));
                            }
                          }}
                          className="w-4 h-4 text-blue border-primary rounded focus:ring-blue"
                        />
                        <span className="text-sm text-primary">{lang.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Custom Text Tab */}
          {activeTab === 'customText' && (
            <div className="space-y-lg">
              <div>
                <h3 className="text-base font-semibold text-primary mb-md">{t('settings.headerText', 'Header Text')}</h3>
                <div className="space-y-md">
                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('settings.headerText1', 'Header Text 1')}
                    </label>
                    <input
                      type="text"
                      value={formData.header_text_1 || ''}
                      onChange={(e) => updateField('header_text_1', e.target.value)}
                      className="input w-full"
                      placeholder={t('settings.headerLinePlaceholder', 'Header line {{number}}', { number: 1 })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('settings.headerText2', 'Header Text 2')}
                    </label>
                    <input
                      type="text"
                      value={formData.header_text_2 || ''}
                      onChange={(e) => updateField('header_text_2', e.target.value)}
                      className="input w-full"
                      placeholder={t('settings.headerLinePlaceholder', 'Header line {{number}}', { number: 2 })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('settings.headerText3', 'Header Text 3')}
                    </label>
                    <input
                      type="text"
                      value={formData.header_text_3 || ''}
                      onChange={(e) => updateField('header_text_3', e.target.value)}
                      className="input w-full"
                      placeholder={t('settings.headerLinePlaceholder', 'Header line {{number}}', { number: 3 })}
                    />
                  </div>
                </div>
              </div>

              <div className="border-t border-primary pt-lg">
                <h3 className="text-base font-semibold text-primary mb-md">{t('settings.customMessages', 'Custom Messages')}</h3>
                <div className="space-y-md">
                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('settings.customRentalPeriodsText')}
                    </label>
                    <textarea
                      value={formData.custom_rental_periods_text || ''}
                      onChange={(e) => updateField('custom_rental_periods_text', e.target.value)}
                      className="input w-full"
                      rows={3}
                      placeholder={t('settings.customRentalPeriodsTextPlaceholder', 'Custom text for rental periods')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('settings.additionalTextCheckout')}
                    </label>
                    <ReactQuill
                      value={formData.additional_text_checkout || ''}
                      onChange={(value) => updateField('additional_text_checkout', value)}
                      modules={quillModules}
                      formats={quillFormats}
                      theme="snow"
                      placeholder={t('settings.additionalTextCheckoutPlaceholder', 'Additional information shown at checkout')}
                    />
                  </div>
                </div>

                {/* Translations Section */}
                {availableLanguages.length > 1 && (
                  <div className="mt-lg">
                    <h4 className="text-sm font-semibold text-primary mb-md">{t('settings.translations', 'Translations')}</h4>

                    {/* Language tabs */}
                    <div className="flex gap-sm mb-lg overflow-x-auto">
                      {availableLanguages.map((lang) => (
                        <button
                          key={lang.code}
                          type="button"
                          onClick={() => setActiveLanguageTab(lang.code)}
                          className={`px-md py-sm rounded-md flex items-center gap-xs whitespace-nowrap transition-colors ${
                            activeLanguageTab === lang.code
                              ? 'bg-blue text-white'
                              : 'bg-secondary text-secondary hover:bg-primary/10'
                          }`}
                        >
                          <span className="text-lg">{lang.flag}</span>
                          <span className="text-sm font-medium">{lang.label}</span>
                        </button>
                      ))}
                    </div>

                    <div className="space-y-md">
                      {activeLanguageTab === defaultLanguage ? (
                        <div className="p-md bg-blue/5 border border-blue/20 rounded-lg space-y-sm">
                          <p className="text-sm text-blue">
                            <strong>{t('settings.customRentalPeriodsText')}:</strong> {formData.custom_rental_periods_text || <em>({t('common.none')})</em>}
                          </p>
                          <div className="text-sm text-blue">
                            <strong>{t('settings.additionalTextCheckout')}:</strong>{' '}
                            {formData.additional_text_checkout ? (
                              <span dangerouslySetInnerHTML={{ __html: formData.additional_text_checkout }} />
                            ) : (
                              <em>({t('common.none')})</em>
                            )}
                          </div>
                          <p className="text-xs text-secondary mt-md">
                            {t('settings.mainLanguageEditAbove', 'This is the main language. Edit the fields above to change these values.')}
                          </p>
                        </div>
                      ) : (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-primary mb-sm">
                              {t('settings.customRentalPeriodsText')} ({activeLanguageTab.toUpperCase()})
                            </label>
                            <textarea
                              value={translations[activeLanguageTab]?.custom_rental_periods_text || ''}
                              onChange={(e) => setTranslations(prev => ({
                                ...prev,
                                [activeLanguageTab]: {
                                  business_name: prev[activeLanguageTab]?.business_name || '',
                                  description: prev[activeLanguageTab]?.description || '',
                                  additional_text_checkout: prev[activeLanguageTab]?.additional_text_checkout || '',
                                  cancellation_policy_description: prev[activeLanguageTab]?.cancellation_policy_description || '',
                                  custom_rental_periods_text: e.target.value
                                }
                              }))}
                              className="input w-full"
                              rows={3}
                              placeholder={`${t('settings.addTranslationVersion', 'Add the {{language}} version', { language: availableLanguages.find(l => l.code === activeLanguageTab)?.label })}`}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-primary mb-sm">
                              {t('settings.additionalTextCheckout')} ({activeLanguageTab.toUpperCase()})
                            </label>
                            <ReactQuill
                              value={translations[activeLanguageTab]?.additional_text_checkout || ''}
                              onChange={(value) => setTranslations(prev => ({
                                ...prev,
                                [activeLanguageTab]: {
                                  business_name: prev[activeLanguageTab]?.business_name || '',
                                  description: prev[activeLanguageTab]?.description || '',
                                  additional_text_checkout: value,
                                  cancellation_policy_description: prev[activeLanguageTab]?.cancellation_policy_description || '',
                                  custom_rental_periods_text: prev[activeLanguageTab]?.custom_rental_periods_text || ''
                                }
                              }))}
                              modules={quillModules}
                              formats={quillFormats}
                              theme="snow"
                              placeholder={`${t('settings.addTranslationVersion', 'Add the {{language}} version', { language: availableLanguages.find(l => l.code === activeLanguageTab)?.label })}`}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Self-Service Settings Tab */}
          {activeTab === 'selfService' && (
            <div className="space-y-lg">
              <div>
                <h3 className="text-base font-semibold text-primary mb-md">{t('settings.operationalSettings', 'Operational Settings')}</h3>
                <div className="space-y-md">
                  <label className="flex items-center gap-md cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.randomize_allocation || false}
                      onChange={(e) => updateField('randomize_allocation', e.target.checked)}
                      className="w-4 h-4 text-blue border-primary rounded focus:ring-blue"
                    />
                    <div>
                      <span className="text-sm font-medium text-primary block">{t('settings.randomizeAllocation', 'Randomize Product Allocation')}</span>
                      <span className="text-xs text-secondary">{t('settings.randomizeAllocationDesc', 'When enabled, inventory items are assigned randomly rather than by allocation order.')}</span>
                    </div>
                  </label>
                </div>
              </div>

              <div className="border-t border-primary pt-lg">
                <h3 className="text-base font-semibold text-primary mb-md">{t('settings.bufferTimes', 'Buffer Times')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-lg">
                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('settings.bufferBefore', 'Buffer Before (minutes)')}
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.buffer_before_minutes || 0}
                      onChange={(e) => updateField('buffer_before_minutes', parseInt(e.target.value) || 0)}
                      className="input w-full"
                      placeholder="0"
                    />
                    <p className="text-xs text-secondary mt-xs">{t('settings.bufferBeforeDesc', 'Time buffer before each booking')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('settings.bufferAfter', 'Buffer After (minutes)')}
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.buffer_after_minutes || 0}
                      onChange={(e) => updateField('buffer_after_minutes', parseInt(e.target.value) || 0)}
                      className="input w-full"
                      placeholder="0"
                    />
                    <p className="text-xs text-secondary mt-xs">{t('settings.bufferAfterDesc', 'Time buffer after each booking')}</p>
                  </div>
                </div>
              </div>

              <div className="border-t border-primary pt-lg">
                <h3 className="text-base font-semibold text-primary mb-md">{t('settings.bookingAutomation', 'Booking Automation')}</h3>
                <div className="space-y-md">
                  <label className="flex items-center gap-md cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.auto_start_bookings || false}
                      onChange={(e) => updateField('auto_start_bookings', e.target.checked)}
                      className="w-4 h-4 text-blue border-primary rounded focus:ring-blue"
                    />
                    <div>
                      <span className="text-sm font-medium text-primary block">{t('settings.autoStartBookings', 'Auto-Start Bookings')}</span>
                      <span className="text-xs text-secondary">{t('settings.autoStartBookingsDesc', 'Automatically move bookings to active status when the scheduled start time has passed')}</span>
                    </div>
                  </label>
                  <label className="flex items-center gap-md cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.auto_complete_bookings || false}
                      onChange={(e) => updateField('auto_complete_bookings', e.target.checked)}
                      className="w-4 h-4 text-blue border-primary rounded focus:ring-blue"
                    />
                    <div>
                      <span className="text-sm font-medium text-primary block">{t('settings.autoCompleteBookings', 'Auto-Complete Bookings')}</span>
                      <span className="text-xs text-secondary">{t('settings.autoCompleteBookingsDesc', 'Automatically move bookings to completed status when the scheduled end time has passed')}</span>
                    </div>
                  </label>
                </div>
              </div>

              <div className="border-t border-primary pt-lg" data-testid="settings-barcode-scanning">
                <h3 className="text-base font-semibold text-primary mb-md">{t('settings.barcodeScanning')}</h3>
                <label className={`flex items-center gap-md ${hasRole('admin') ? 'cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}>
                  <input
                    type="checkbox"
                    checked={(formData as any).barcode_scanning_enabled || false}
                    onChange={(e) => updateField('barcode_scanning_enabled' as keyof MerchantWithCurrency, e.target.checked)}
                    disabled={!hasRole('admin')}
                    className="w-4 h-4 text-blue border-primary rounded focus:ring-blue"
                    data-testid="settings-barcode-scanning-toggle"
                  />
                  <div>
                    <span className="text-sm font-medium text-primary block">{t('settings.barcodeScanningEnable')}</span>
                    <span className="text-xs text-secondary">{t('settings.barcodeScanningDesc')}</span>
                  </div>
                </label>
                {!hasRole('admin') && (
                  <p className="text-xs text-secondary mt-xs">{t('settings.barcodeScanningAdminOnly')}</p>
                )}
                {(formData as any).barcode_scanning_enabled && (formData.auto_start_bookings || formData.auto_complete_bookings) && (
                  <p className="text-xs text-warning mt-sm">{t('settings.barcodeScanningAutomationHint')}</p>
                )}
              </div>
            </div>
          )}

          {/* Payments & Policies Tab */}
          {activeTab === 'paymentsPolicy' && (
            <div className="space-y-lg">
              {/* Stripe Connect Section */}
              <div>
                <h3 className="text-base font-semibold text-primary mb-md">{t('settings.paymentAccount', 'Payment Account')}</h3>
                <p className="text-sm text-secondary mb-lg">
                  {t('settings.connectStripeDesc', 'Connect your Stripe account to accept payments from customers.')}
                </p>

                {stripeLoading ? (
                  <div className="flex items-center gap-sm text-secondary">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">{t('settings.loadingPaymentStatus', 'Loading payment status...')}</span>
                  </div>
                ) : stripeStatus?.error ? (
                  // Error state - invalid account ID
                  <div className="p-md bg-danger/10 border border-danger/20 rounded-lg">
                    <div className="flex items-center gap-sm mb-sm">
                      <AlertCircle className="w-5 h-5 text-danger" />
                      <span className="font-medium text-danger">{t('settings.configurationError', 'Configuration Error')}</span>
                    </div>
                    <p className="text-sm text-secondary mb-md">
                      {stripeStatus.error}
                    </p>
                    <button
                      onClick={fetchStripeStatus}
                      disabled={stripeLoading}
                      className="btn btn-secondary text-sm flex items-center gap-sm"
                    >
                      <RefreshCw className={`w-4 h-4 ${stripeLoading ? 'animate-spin' : ''}`} />
                      {t('settings.retry', 'Retry')}
                    </button>
                  </div>
                ) : stripeStatus?.connected ? (
                  // Connected state
                  <div className="space-y-md">
                    {stripeStatus.chargesEnabled && stripeStatus.payoutsEnabled ? (
                      // Fully active
                      <div className="p-md bg-success/10 border border-success/20 rounded-lg">
                        <div className="flex items-center gap-sm mb-sm">
                          <CheckCircle className="w-5 h-5 text-success" />
                          <span className="font-medium text-success">{t('settings.paymentsActive', 'Payments Active')}</span>
                        </div>
                        <p className="text-sm text-secondary mb-md">
                          {t('settings.stripeConnected', 'Your Stripe account is connected and ready to accept payments.')}
                        </p>
                        <div className="flex flex-wrap gap-sm">
                          {stripeStatus.dashboardUrl && (
                            <a
                              href={stripeStatus.dashboardUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-secondary text-sm flex items-center gap-sm whitespace-nowrap"
                            >
                              <ExternalLink className="w-4 h-4 flex-shrink-0" />
                              {t('settings.openStripeDashboard', 'Open Stripe Dashboard')}
                            </a>
                          )}
                          <button
                            onClick={fetchStripeStatus}
                            disabled={stripeLoading}
                            className="btn btn-secondary text-sm flex items-center gap-sm whitespace-nowrap"
                          >
                            <RefreshCw className={`w-4 h-4 flex-shrink-0 ${stripeLoading ? 'animate-spin' : ''}`} />
                            {t('settings.refreshStatus', 'Refresh Status')}
                          </button>
                        </div>

                        {/* Payment Methods Selector */}
                        <div className="mt-lg pt-lg border-t border-success/20 min-w-0">
                          <h4 className="text-sm font-semibold text-primary mb-sm">{t('settings.acceptedPaymentMethods', 'Accepted Payment Methods')}</h4>
                          <p className="text-sm text-secondary mb-md">
                            {t('settings.paymentMethodsDesc', 'Select which payment methods to accept from customers. Only methods enabled in your Stripe account are shown.')}
                          </p>

                          {stripeStatus.activePaymentMethods && stripeStatus.activePaymentMethods.length > 0 ? (
                            <>
                              {/* Show warning if saved methods are no longer supported */}
                              {formData.payment_method_types?.some(
                                (method) => !stripeStatus.activePaymentMethods?.find(m => m.value === method)
                              ) && (
                                <div className="p-sm bg-warning/10 border border-warning/20 rounded-lg mb-md flex items-start gap-sm">
                                  <AlertCircle className="w-4 h-4 text-warning flex-shrink-0 mt-0.5" />
                                  <p className="text-sm text-warning">
                                    {t('settings.paymentMethodsWarning', 'Some saved payment methods are no longer available in your Stripe account and will be removed when you save.')}
                                  </p>
                                </div>
                              )}

                              <div className="flex flex-wrap gap-sm">
                                {stripeStatus.activePaymentMethods.map((method) => {
                                  const isSelected = formData.payment_method_types?.includes(method.value) ?? (method.value === 'card');
                                  return (
                                    <button
                                      key={method.value}
                                      type="button"
                                      onClick={() => {
                                        const currentMethods = formData.payment_method_types || ['card'];
                                        const newMethods = isSelected
                                          ? currentMethods.filter(m => m !== method.value)
                                          : [...currentMethods, method.value];
                                        // Ensure at least 'card' is always selected
                                        if (newMethods.length === 0) {
                                          updateField('payment_method_types', ['card']);
                                        } else {
                                          updateField('payment_method_types', newMethods);
                                        }
                                      }}
                                      className={`px-md py-sm rounded-lg border transition-all ${
                                        isSelected
                                          ? 'bg-blue text-white border-blue'
                                          : 'bg-secondary text-secondary border-primary hover:border-blue'
                                      }`}
                                    >
                                      <span className="text-sm font-medium">{method.label}</span>
                                    </button>
                                  );
                                })}
                              </div>

                              <p className="text-xs text-secondary mt-md">
                                {t('settings.enableMorePaymentMethods', 'To enable additional payment methods, configure them in your')}{' '}
                                <a
                                  href="https://dashboard.stripe.com/settings/payment_methods"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue hover:underline"
                                >
                                  {t('settings.stripeDashboard', 'Stripe Dashboard')}
                                </a>
                                .
                              </p>
                            </>
                          ) : (
                            <p className="text-sm text-secondary italic">
                              {t('settings.noPaymentMethods', 'No payment methods available. Please complete your Stripe account setup.')}
                            </p>
                          )}
                        </div>
                      </div>
                    ) : stripeStatus.detailsSubmitted ? (
                      // Pending verification
                      <div className="p-md bg-warning/10 border border-warning/20 rounded-lg">
                        <div className="flex items-center gap-sm mb-sm">
                          <AlertCircle className="w-5 h-5 text-warning" />
                          <span className="font-medium text-warning">{t('settings.verificationPending', 'Verification Pending')}</span>
                        </div>
                        <p className="text-sm text-secondary mb-md">
                          {t('settings.verificationPendingDesc', 'Your account information has been submitted and is being verified by Stripe. This usually takes 1-2 business days.')}
                        </p>
                        <div className="flex flex-wrap gap-sm">
                          <button
                            onClick={fetchStripeStatus}
                            disabled={stripeLoading}
                            className="btn btn-secondary text-sm flex items-center gap-sm whitespace-nowrap"
                          >
                            <RefreshCw className={`w-4 h-4 flex-shrink-0 ${stripeLoading ? 'animate-spin' : ''}`} />
                            {t('settings.checkStatus', 'Check Status')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Onboarding incomplete
                      <div className="p-md bg-blue/10 border border-blue/20 rounded-lg">
                        <div className="flex items-center gap-sm mb-sm">
                          <AlertCircle className="w-5 h-5 text-blue" />
                          <span className="font-medium text-blue">{t('settings.setupIncomplete', 'Setup Incomplete')}</span>
                        </div>
                        <p className="text-sm text-secondary mb-md">
                          {t('settings.setupIncompleteDesc', 'You started the payment setup but didn\'t complete it. Please continue to finish setting up your account.')}
                        </p>
                        <button
                          onClick={handleStripeActivation}
                          disabled={stripeActivating}
                          className="btn btn-primary flex items-center gap-sm"
                        >
                          {stripeActivating ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              {t('settings.generatingLink', 'Generating Link...')}
                            </>
                          ) : (
                            <>
                              <CreditCard className="w-4 h-4" />
                              {t('settings.continueSetup', 'Continue Setup')}
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  // Not connected state
                  <div className="p-md bg-secondary border border-primary rounded-lg">
                    <div className="flex items-center gap-sm mb-sm">
                      <CreditCard className="w-5 h-5 text-secondary" />
                      <span className="font-medium text-primary">{t('settings.noPaymentAccount', 'No Payment Account Connected')}</span>
                    </div>
                    <p className="text-sm text-secondary mb-md">
                      {t('settings.connectStripeDesc', 'To accept payments from your customers, you need to connect a Stripe account. Click the button below to set up your payment account.')}
                    </p>
                    <button
                      onClick={handleStripeActivation}
                      disabled={stripeActivating}
                      className="btn btn-primary flex items-center gap-sm"
                    >
                      {stripeActivating ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          {t('settings.generatingLink', 'Generating Link...')}
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4" />
                          {t('settings.activatePayments', 'Activate Payments')}
                        </>
                      )}
                    </button>
                    {errors.stripe && (
                      <p className="text-danger text-sm mt-md flex items-center gap-xs">
                        <AlertCircle className="w-4 h-4" />
                        {errors.stripe}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* PayPal Direct Section */}
              <div className="border-t border-primary pt-lg">
                <h3 className="text-base font-semibold text-primary mb-md">{t('settings.paypalDirect', 'PayPal Direct Payments')}</h3>
                <p className="text-sm text-secondary mb-lg">
                  {t('settings.paypalDirectDesc', 'Connect your PayPal Business account to receive PayPal payments directly from customers.')}
                </p>

                {paypalStatus.loading ? (
                  <div className="flex items-center gap-sm text-secondary">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">{t('settings.loadingPaypalStatus', 'Loading PayPal status...')}</span>
                  </div>
                ) : paypalStatus.connected && paypalStatus.isActive ? (
                  // Connected state
                  <div className="p-md bg-success/10 border border-success/20 rounded-lg">
                    <div className="flex items-center gap-sm mb-sm">
                      <CheckCircle className="w-5 h-5 text-success" />
                      <span className="font-medium text-success">{t('settings.paypalConnected', 'PayPal Connected')}</span>
                    </div>
                    <div className="text-sm text-secondary space-y-xs mb-md">
                      <p>{t('settings.paypalMerchantId', 'Merchant ID')}: <span className="font-mono text-primary">{paypalStatus.paypalMerchantId}</span></p>
                      {paypalStatus.paypalEmail && (
                        <p>{t('settings.paypalEmail', 'Email')}: <span className="text-primary">{paypalStatus.paypalEmail}</span></p>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-sm mb-md">
                      <label className="flex items-center gap-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.paypal_direct_enabled || false}
                          onChange={(e) => {
                            if (e.target.checked && formData.deposits_enabled) {
                              setErrors({ paypal: t('settings.paypalDepositsConflict', 'Cannot enable PayPal Direct when deposits are active. Disable deposits first.') });
                              return;
                            }
                            if (e.target.checked && formData.off_session_payments) {
                              setErrors({ paypal: t('settings.paypalOffSessionConflict', 'Cannot enable PayPal Direct when off-session payments are active. Disable off-session payments first.') });
                              return;
                            }
                            updateField('paypal_direct_enabled', e.target.checked);
                          }}
                          disabled={(formData.deposits_enabled || formData.off_session_payments) || false}
                          className="w-4 h-4 text-blue border-primary rounded focus:ring-blue"
                        />
                        <span className="text-sm font-medium text-primary">{t('settings.enablePaypalDirect', 'Enable PayPal Direct Payments')}</span>
                      </label>
                    </div>
                    {formData.deposits_enabled && (
                      <p className="text-xs text-warning flex items-center gap-xs mb-md">
                        <AlertCircle className="w-3.5 h-3.5" />
                        {t('settings.paypalDepositsConflict', 'Cannot enable PayPal Direct when deposits are active. Disable deposits first.')}
                      </p>
                    )}
                    {formData.off_session_payments && (
                      <p className="text-xs text-warning flex items-center gap-xs mb-md">
                        <AlertCircle className="w-3.5 h-3.5" />
                        {t('settings.paypalOffSessionConflict', 'Cannot enable PayPal Direct when off-session payments are active. Disable off-session payments first.')}
                      </p>
                    )}

                    <button
                      onClick={handlePaypalDisconnect}
                      disabled={paypalDisconnecting}
                      className="btn btn-danger text-sm flex items-center gap-sm"
                    >
                      {paypalDisconnecting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                      {t('settings.disconnectPaypal', 'Disconnect PayPal')}
                    </button>
                  </div>
                ) : (
                  // Not connected state — manual onboarding form
                  <div className="p-md bg-secondary border border-primary rounded-lg">
                    <p className="text-sm text-secondary mb-md">
                      {t('settings.paypalConnectInstructions', 'Enter your PayPal Business Merchant ID to receive payments directly. You can find this in your PayPal Business account under Account Settings.')}
                    </p>
                    <div className="space-y-sm mb-md">
                      <div>
                        <label className="block text-sm font-medium text-primary mb-xs">
                          {t('settings.paypalMerchantId', 'PayPal Merchant ID')} *
                        </label>
                        <input
                          type="text"
                          value={paypalMerchantIdInput}
                          onChange={(e) => setPaypalMerchantIdInput(e.target.value)}
                          placeholder="e.g. ABC123XYZ4567"
                          className="input w-full text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-primary mb-xs">
                          {t('settings.paypalEmail', 'PayPal Email')} ({t('common.optional', 'optional')})
                        </label>
                        <input
                          type="email"
                          value={paypalEmailInput}
                          onChange={(e) => setPaypalEmailInput(e.target.value)}
                          placeholder="business@example.com"
                          className="input w-full text-sm"
                        />
                      </div>
                    </div>
                    <button
                      onClick={handlePaypalConnect}
                      disabled={paypalConnecting || !paypalMerchantIdInput.trim()}
                      className="btn btn-primary flex items-center gap-sm"
                    >
                      {paypalConnecting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          {t('settings.connecting', 'Connecting...')}
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4" />
                          {t('settings.connectPaypal', 'Connect PayPal')}
                        </>
                      )}
                    </button>
                    {errors.paypal && (
                      <p className="text-danger text-sm mt-md flex items-center gap-xs">
                        <AlertCircle className="w-4 h-4" />
                        {errors.paypal}
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="border-t border-primary pt-lg">
                <h3 className="text-base font-semibold text-primary mb-md">{t('settings.paymentSettings', 'Payment Settings')}</h3>
                <div className="space-y-md">
                  <label className="flex items-center gap-md cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.deposits_enabled || false}
                      onChange={(e) => {
                        if (e.target.checked && formData.paypal_direct_enabled) {
                          setErrors({ paypal: t('settings.depositsPaypalConflict', 'Cannot enable deposits when PayPal Direct is active. Disable PayPal Direct first.') });
                          return;
                        }
                        updateField('deposits_enabled', e.target.checked);
                      }}
                      disabled={formData.paypal_direct_enabled || false}
                      className="w-4 h-4 text-blue border-primary rounded focus:ring-blue"
                    />
                    <div>
                      <span className="text-sm font-medium text-primary block">{t('settings.enableDeposits', 'Enable Deposits')}</span>
                      <span className="text-xs text-secondary">{t('settings.enableDepositsDesc', 'Allow customers to pay deposits instead of full amount')}</span>
                      {formData.paypal_direct_enabled && (
                        <span className="text-xs text-warning flex items-center gap-xs mt-xs">
                          <AlertCircle className="w-3 h-3" />
                          {t('settings.depositsPaypalConflict', 'Cannot enable deposits when PayPal Direct is active. Disable PayPal Direct first.')}
                        </span>
                      )}
                    </div>
                  </label>
                  {formData.deposits_enabled && (
                    <div className="ml-8 space-y-sm">
                      <div>
                        <span className="text-sm font-medium text-primary block">{t('settings.depositRules')}</span>
                        <span className="text-xs text-secondary">{t('settings.depositRulesDesc', 'Configure deposit amounts per product category')}</span>
                      </div>
                      {depositRules.map((rule, index) => (
                        <div key={index} className="flex items-center gap-sm">
                          <input
                            type="text"
                            value={rule.category_slug}
                            onChange={(e) => updateDepositRule(index, 'category_slug', e.target.value)}
                            placeholder={t('settings.categorySlugPlaceholder', 'Category slug (e.g. electric-bike)')}
                            className="input flex-1 text-sm"
                          />
                          <div className="relative flex items-center">
                            <span className="absolute left-2 text-sm text-secondary pointer-events-none">
                              {CURRENCIES.find(c => c.code === formData.currency_code)?.symbol || '€'}
                            </span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={rule.amount ? (rule.amount / 100).toFixed(2) : ''}
                              onChange={(e) => {
                                const cents = Math.round(parseFloat(e.target.value || '0') * 100);
                                updateDepositRule(index, 'amount', cents);
                              }}
                              placeholder="0.00"
                              className="input w-32 text-sm pl-6"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removeDepositRule(index)}
                            className="text-danger hover:text-danger/80"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addDepositRule}
                        className="text-sm text-blue hover:text-blue/80 flex items-center gap-xs"
                      >
                        <Plus className="w-4 h-4" />
                        {t('settings.addRule', 'Add Rule')}
                      </button>
                    </div>
                  )}
                  <label className="flex items-center gap-md cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.off_session_payments || false}
                      onChange={(e) => {
                        if (e.target.checked && formData.paypal_direct_enabled) {
                          setErrors({ paypal: t('settings.offSessionPaypalConflict', 'Cannot enable off-session payments when PayPal Direct is active. Disable PayPal Direct first.') });
                          return;
                        }
                        updateField('off_session_payments', e.target.checked);
                      }}
                      disabled={formData.paypal_direct_enabled || false}
                      className="w-4 h-4 text-blue border-primary rounded focus:ring-blue"
                    />
                    <div>
                      <span className="text-sm font-medium text-primary block">{t('settings.offSessionPayments', 'Off-Session Payments')}</span>
                      <span className="text-xs text-secondary">{t('settings.offSessionPaymentsDesc', 'Enable payments without customer presence')}</span>
                      {formData.paypal_direct_enabled && (
                        <span className="text-xs text-warning flex items-center gap-xs mt-xs">
                          <AlertCircle className="w-3 h-3" />
                          {t('settings.offSessionPaypalConflict', 'Cannot enable off-session payments when PayPal Direct is active. Disable PayPal Direct first.')}
                        </span>
                      )}
                    </div>
                  </label>
                </div>
              </div>

              <div className="border-t border-primary pt-lg">
                <h3 className="text-base font-semibold text-primary mb-md">{t('settings.legalPolicies', 'Legal & Policies')}</h3>
                <div className="space-y-md">
                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('settings.termsAndConditions')} PDF
                    </label>
                    {formData.terms_pdf_url && (
                      <div className="mb-sm p-sm bg-secondary rounded-lg flex items-center justify-between">
                        <a
                          href={formData.terms_pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue hover:underline truncate flex-1"
                        >
                          {formData.terms_pdf_url.split('/').pop()}
                        </a>
                        <button
                          type="button"
                          onClick={() => updateField('terms_pdf_url', null)}
                          className="text-danger hover:text-danger/80 ml-sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    <div className="flex gap-md">
                      <label className="btn btn-secondary cursor-pointer flex items-center gap-sm flex-1">
                        <Upload className="w-4 h-4" />
                        {uploadingTerms ? t('settings.uploading', 'Uploading...') : t('settings.uploadPdf', 'Upload PDF')}
                        <input
                          type="file"
                          accept="application/pdf"
                          onChange={handleTermsUpload}
                          disabled={uploadingTerms}
                          className="hidden"
                        />
                      </label>
                      <span className="text-xs text-secondary self-center">{t('common.or', 'or')}</span>
                      <input
                        type="url"
                        value={formData.terms_pdf_url || ''}
                        onChange={(e) => updateField('terms_pdf_url', e.target.value)}
                        className="input flex-1"
                        placeholder={t('settings.enterUrlPlaceholder', 'Enter URL...')}
                      />
                    </div>
                    {errors.terms_pdf && (
                      <p className="text-danger text-xs mt-xs flex items-center gap-xs">
                        <AlertCircle className="w-3 h-3" />
                        {errors.terms_pdf}
                      </p>
                    )}
                    <p className="text-xs text-secondary mt-xs">{t('settings.uploadPdfHint', 'Upload a PDF or provide a URL (max 10MB)')}</p>
                  </div>

                  {/* Cancellation Policy Description (customer-facing prose, translatable) */}
                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('settings.cancellationPolicyDescription', 'Cancellation Policy Description')}
                    </label>
                    <textarea
                      value={formData.cancellation_policy_description || ''}
                      onChange={(e) => updateField('cancellation_policy_description', e.target.value)}
                      className="input w-full"
                      rows={4}
                      placeholder={t('settings.cancellationPolicyDescriptionPlaceholder', 'Cancellation/refund terms shown to customers')}
                    />

                    {/* Translations Section */}
                    {availableLanguages.length > 1 && (
                      <div className="mt-lg">
                        <h4 className="text-sm font-semibold text-primary mb-md">{t('settings.translations', 'Translations')}</h4>

                        {/* Language tabs */}
                        <div className="flex gap-sm mb-lg overflow-x-auto">
                          {availableLanguages.map((lang) => (
                            <button
                              key={lang.code}
                              type="button"
                              onClick={() => setActiveLanguageTab(lang.code)}
                              className={`px-md py-sm rounded-md flex items-center gap-xs whitespace-nowrap transition-colors ${
                                activeLanguageTab === lang.code
                                  ? 'bg-blue text-white'
                                  : 'bg-secondary text-secondary hover:bg-primary/10'
                              }`}
                            >
                              <span className="text-lg">{lang.flag}</span>
                              <span className="text-sm font-medium">{lang.label}</span>
                            </button>
                          ))}
                        </div>

                        <div className="space-y-md">
                          {activeLanguageTab === defaultLanguage ? (
                            <div className="p-md bg-blue/5 border border-blue/20 rounded-lg space-y-sm">
                              <p className="text-sm text-blue">
                                <strong>{t('settings.cancellationPolicyDescription', 'Cancellation Policy Description')}:</strong> {formData.cancellation_policy_description || <em>({t('common.none')})</em>}
                              </p>
                              <p className="text-xs text-secondary mt-md">
                                {t('settings.mainLanguageEditAbove', 'This is the main language. Edit the fields above to change these values.')}
                              </p>
                            </div>
                          ) : (
                            <div>
                              <label className="block text-sm font-medium text-primary mb-sm">
                                {t('settings.cancellationPolicyDescription', 'Cancellation Policy Description')} ({activeLanguageTab.toUpperCase()})
                              </label>
                              <textarea
                                value={translations[activeLanguageTab]?.cancellation_policy_description || ''}
                                onChange={(e) => setTranslations(prev => ({
                                  ...prev,
                                  [activeLanguageTab]: {
                                    business_name: prev[activeLanguageTab]?.business_name || '',
                                    description: prev[activeLanguageTab]?.description || '',
                                    additional_text_checkout: prev[activeLanguageTab]?.additional_text_checkout || '',
                                    cancellation_policy_description: e.target.value,
                                    custom_rental_periods_text: prev[activeLanguageTab]?.custom_rental_periods_text || ''
                                  }
                                }))}
                                className="input w-full"
                                rows={4}
                                placeholder={`${t('settings.addTranslationVersion', 'Add the {{language}} version', { language: availableLanguages.find(l => l.code === activeLanguageTab)?.label })}`}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-sm">
                      <label className="block text-sm font-medium text-primary">
                        {t('settings.cancellationPolicyRules', 'Cancellation Policy Rules')}
                      </label>
                      <button
                        type="button"
                        onClick={addCancellationRule}
                        className="text-blue hover:text-blue/80 text-sm font-medium flex items-center gap-xs"
                      >
                        <Plus className="w-4 h-4" />
                        {t('settings.addRule', 'Add Rule')}
                      </button>
                    </div>
                    <p className="text-xs text-secondary mb-md">
                      {t('settings.cancellationRulesDesc', 'Define refund rules based on cancellation timing.')} {t('settings.currency')}: {formData.currency_code || 'USD'}
                    </p>

                    {cancellationRules.length === 0 ? (
                      <div className="text-center p-lg bg-secondary rounded-lg">
                        <FileText className="w-8 h-8 text-secondary mx-auto mb-sm" />
                        <p className="text-sm text-secondary">{t('settings.noCancellationRules', 'No cancellation rules defined')}</p>
                        <button
                          type="button"
                          onClick={addCancellationRule}
                          className="btn btn-secondary mt-md"
                        >
                          {t('settings.addFirstRule', 'Add First Rule')}
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-md">
                        {cancellationRules.map((rule, index) => (
                          <div key={index} className="card p-md border border-primary">
                            <div className="flex items-start justify-between mb-sm">
                              <span className="text-sm font-medium text-primary">{t('settings.ruleNumber', 'Rule {{number}}', { number: index + 1 })}</span>
                              <button
                                type="button"
                                onClick={() => removeCancellationRule(index)}
                                className="text-danger hover:text-danger/80"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="space-y-sm">
                              <div className="grid grid-cols-2 gap-sm">
                                <div>
                                  <label className="block text-xs font-medium text-secondary mb-xs">
                                    {t('settings.refundPercent', 'Refund %')}
                                  </label>
                                  <NumberInput
                                    value={rule.refund_percentage}
                                    onChange={(val) =>
                                      updateCancellationRule(index, 'refund_percentage', val)
                                    }
                                    mode="int"
                                    min={0}
                                    max={100}
                                    className="input w-full text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-secondary mb-xs">
                                    {t('settings.hoursBeforeStart', 'Hours Before Start')}
                                  </label>
                                  <NumberInput
                                    value={rule.hours_before_start}
                                    onChange={(val) =>
                                      updateCancellationRule(index, 'hours_before_start', val)
                                    }
                                    mode="int"
                                    min={0}
                                    className="input w-full text-sm"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Return App Tab */}
          {activeTab === 'returnApp' && merchant && (
            <ReturnAppSettings
              merchantId={merchant.id}
              onSaveSuccess={() => {
                setSuccessMessage(t('settings.returnAppSaved', 'Return app configuration saved successfully!'));
                setTimeout(() => setSuccessMessage(''), 3000);
              }}
              onSaveError={(error) => {
                setErrors({ returnApp: error });
              }}
            />
          )}
        </div>
      </div>

      {/* Mobile Save Button - hidden on Return App tab which has its own save */}
      {activeTab !== 'returnApp' && (
        <div className="lg:hidden sticky bottom-0 p-md bg-primary border-t border-primary">
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn btn-primary w-full flex items-center justify-center gap-sm"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('common.saving')}
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {t('settings.saveSettings')}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
